import { DateTime } from 'luxon'
import { GetPsyAliveListData, ProfilePsyAliveNewSearch } from '../../types'
import { autoCapitalizeName } from '@/utils/autoCapitalizeName'

export class PsyAliveListMapper {
  toDomain(professional: ProfilePsyAliveNewSearch) {
    return {
      professionalFormatted: {
        id: professional?.professional?.id,
        name: autoCapitalizeName(professional?.professional?.name),
        specialty: professional?.professional?.specialtyName,
        council: PsyAliveListMapper.formatProfessionalCouncil(
          professional?.professional,
        ),
        photo: professional?.professional?.urlPhoto,
        about: professional?.professional?.graduation ?? '-',
        councilType: professional?.professional?.councilType,
        duration: professional?.professional?.appointmentDuration,
        price: professional?.professional?.appointmentPrice,
        reviews: {
          average: professional?.review?.rating,
          total: professional?.review?.total,
          label: 'avaliações',
        },
        workingFields:
          professional?.professional?.themes?.map(theme => ({
            label: theme,
          })) || [],
        appointments: {
          total: professional?.professional?.totalAppointments,
          label: 'atendimentos',
        },
      },
      ...professional,
    }
  }

  toPersistence(
    data: GetPsyAliveListData,
    isPsychologistAlreadyMigrated: boolean,
  ) {
    const payload = {
      idPatient: data?.idPatient,
      genericFilter: data?.term,
      themeIds: data.reasons?.map(reason => reason.key),
      appointmentDuration: data.durations?.map(duration => duration.key),
      ethnicityIds: data.ethnicities?.map(ethnicity => ethnicity.key),
      specialtyIds: data.specialties?.map(specialty => specialty.key),
      genders: data.genders?.map(gender => gender.key),
      state: data.state?.value,
      city: data.city?.value,
      librasAssistance: data?.providesSignLanguageService,
      availabilityStartDate: PsyAliveListMapper.formatJSDateToApiPattern(
        data?.availabilityStart,
      ),
      availabilityEndDate: PsyAliveListMapper.formatJSDateToApiPattern(
        data?.availabilityEnd,
      ),
      approachIds: data.approaches?.map(approach => approach.key),
      page: data?.page ?? 1,
      executeCount: false,
      limit: 2,
      professionalType: 'PSICOLOGO',
      isPsychologistAlreadyMigrated,
    }

    const isValid = value =>
      value !== undefined &&
      value !== null &&
      !(Array.isArray(value) && value?.length === 0) &&
      !(typeof value === 'string' && value.trim() === '')

    return Object.keys(payload)?.reduce((acc, key) => {
      if (isValid(payload[key])) {
        acc[key] = payload[key]
      }
      return acc
    }, {})
  }

  private static formatProfessionalCouncil(
    professional: ProfilePsyAliveNewSearch['professional'],
  ) {
    if (!professional?.councilType) return ''

    if (professional?.councilUf) {
      return `${professional?.councilType} ${
        professional?.councilNumer || ''
      } (${professional?.councilUf})`
    }

    return `${professional?.councilType} ${professional?.councilNumer || ''}`
  }

  private static formatJSDateToApiPattern(date: Date) {
    if (!date) return ''
    return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
  }
}

export default new PsyAliveListMapper()
